// =====================================================================================
// INVRT Radar — web console — REST client
//
// One place builds a request to the api Edge Function, and one place turns a failure into
// an ApiError. Nothing else in the console calls fetch().
//
// AUTH AND SCOPE, exactly as supabase/functions/_shared/auth.ts resolves them:
//   - Identity: `Authorization: Bearer <supabase user JWT>`. The api function checks
//     X-API-Key FIRST and only then the bearer token — the browser never holds an API
//     key, so we send the bearer alone.
//   - Org scope: `X-Org-Id`. resolveOrgScope() REQUIRES this header for a user caller
//     (a user may belong to several organisations) and then VERIFIES it against
//     `memberships` through the caller's own RLS-scoped client. The header is a
//     declaration of which org the user is acting in, never a grant — sending one the
//     user is not a member of answers 403 forbidden.
//
// Base URL: `${VITE_SUPABASE_URL}/functions/v1/api/v1`. The deployed function receives its
// own slug as the leading path segment and normalises it (router.ts's findRoute), which is
// why the doubled `/api/v1` is correct rather than a typo.
// =====================================================================================

import type { ErrorEnvelope, ValidationFieldError } from "./types";

/** Thrown for every non-2xx answer, and for a transport failure. */
export class ApiError extends Error {
  /** The stable snake_case token — the ONLY thing to branch on. */
  readonly token: string;
  readonly httpStatus: number;
  /** Present on every response the api function produced; absent on a transport failure. */
  readonly correlationId: string | null;
  readonly details: Record<string, unknown> | undefined;

  constructor(init: {
    token: string;
    message: string;
    httpStatus: number;
    correlationId: string | null;
    details?: Record<string, unknown>;
  }) {
    super(init.message);
    this.name = "ApiError";
    this.token = init.token;
    this.httpStatus = init.httpStatus;
    this.correlationId = init.correlationId;
    this.details = init.details;
  }

  /** `validation_failed` carries its field errors in details.fields. */
  fieldErrors(): ValidationFieldError[] {
    const fields = this.details?.["fields"];
    if (!Array.isArray(fields)) return [];
    return fields.filter(
      (f): f is ValidationFieldError =>
        typeof f === "object" && f !== null && "field" in f && "message" in f,
    );
  }

  /** `suppressed_contact` echoes back the caller's OWN refs, which are safe to show. */
  suppressedRefs(): string[] {
    const refs = this.details?.["suppressed"];
    if (!Array.isArray(refs)) return [];
    return refs.filter((r): r is string => typeof r === "string");
  }
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Where the api function lives. Trailing slashes on the env value are tolerated. */
export function apiBaseUrl(): string {
  const raw = import.meta.env.VITE_SUPABASE_URL;
  if (!raw) {
    throw new ApiError({
      token: "config_missing",
      message: "VITE_SUPABASE_URL is not set for this build.",
      httpStatus: 0,
      correlationId: null,
    });
  }
  return `${raw.replace(/\/+$/, "")}/functions/v1/api/v1`;
}

/** Resolved per request so a token refresh is picked up without re-creating the client. */
export type TokenSource = () => Promise<string | null>;

export interface ApiClientOptions {
  getAccessToken: TokenSource;
  /** The organisation the user is acting in. Null before one is chosen. */
  orgId: string | null;
  /** Injected in tests. Defaults to the global fetch. */
  fetchImpl?: typeof fetch;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** Serialised as JSON. Omit for a bodyless request. */
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  /** Set false for the two routes registered auth:"none" (health, openapi.json). */
  requiresOrg?: boolean;
  /** Set false for platform-operator routes that are not scoped to the selected organisation. */
  includeOrg?: boolean;
  signal?: AbortSignal;
  /** Ask for a raw text answer (GET /v1/ledger/export?format=csv). */
  responseType?: "json" | "text";
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const url = new URL(`${apiBaseUrl()}${path}`);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined) url.searchParams.set(key, String(value));
  }
  return url.toString();
}

async function toApiError(response: Response): Promise<ApiError> {
  const correlationId = response.headers.get("X-Correlation-Id");
  let envelope: ErrorEnvelope | null = null;
  try {
    envelope = (await response.json()) as ErrorEnvelope;
  } catch {
    // A response with no JSON body (a gateway 502, an aborted stream). The status is all
    // we have, and inventing a token would be worse than admitting that.
    envelope = null;
  }

  const error = envelope?.error;
  return new ApiError({
    token: error?.token ?? "internal_error",
    message: error?.message ?? `The request failed with status ${response.status}.`,
    httpStatus: response.status,
    correlationId: error?.correlation_id ?? correlationId,
    ...(error?.details ? { details: error.details } : {}),
  });
}

export class ApiClient {
  private readonly getAccessToken: TokenSource;
  private readonly fetchImpl: typeof fetch;
  readonly orgId: string | null;

  constructor(options: ApiClientOptions) {
    this.getAccessToken = options.getAccessToken;
    this.orgId = options.orgId;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const requiresOrg = options.requiresOrg ?? true;
    const token = await this.getAccessToken();

    if (!token) {
      throw new ApiError({
        token: "unauthenticated",
        message: "There is no active session.",
        httpStatus: 401,
        correlationId: null,
      });
    }

    if (requiresOrg && !this.orgId) {
      // resolveOrgScope() answers 403 without this header. Refusing here keeps the
      // failure honest and local rather than spending a round trip to learn it.
      throw new ApiError({
        token: "forbidden",
        message: "Choose an organisation before making this request.",
        httpStatus: 403,
        correlationId: null,
      });
    }

    const headers: Record<string, string> = {
      Authorization: `Bearer ${token}`,
      Accept: options.responseType === "text" ? "text/csv, text/plain" : "application/json",
    };
    if (this.orgId && options.includeOrg !== false) headers["X-Org-Id"] = this.orgId;
    if (options.body !== undefined) headers["Content-Type"] = "application/json";

    let response: Response;
    try {
      response = await this.fetchImpl(buildUrl(path, options.query), {
        method: options.method ?? "GET",
        headers,
        ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
        ...(options.signal ? { signal: options.signal } : {}),
      });
    } catch (cause) {
      // A transport failure never reached the api function, so there is no correlation id
      // and — importantly for the money path — no possibility that a burn happened.
      throw new ApiError({
        token: "provider_unavailable",
        message: "Radar could not be reached. Check your connection and try again.",
        httpStatus: 0,
        correlationId: null,
        details: { cause: String(cause) },
      });
    }

    if (!response.ok) throw await toApiError(response);

    if (options.responseType === "text") {
      return (await response.text()) as unknown as T;
    }
    if (response.status === 204) return undefined as unknown as T;
    return (await response.json()) as T;
  }

  get<T>(path: string, options: Omit<RequestOptions, "method" | "body"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "GET" });
  }

  post<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "POST", ...(body === undefined ? {} : { body }) });
  }

  put<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PUT", ...(body === undefined ? {} : { body }) });
  }

  patch<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "PATCH", ...(body === undefined ? {} : { body }) });
  }

  delete<T>(path: string, body?: unknown, options: Omit<RequestOptions, "method"> = {}): Promise<T> {
    return this.request<T>(path, { ...options, method: "DELETE", ...(body === undefined ? {} : { body }) });
  }
}
